import { Loop } from "@carbon/icons-react";
import { Center, Pad, PadBox } from "component/basics";
import { CTAButton, SubtleButton } from "component/button";
import { colorButtonAccent, colorSurfaceSecondary } from "component/color";
import { Comment, CommentConfig, CommentsInput, commentTable } from "component/comment";
import { PollResult } from "component/poll";
import { RatingSelectorReworked } from "open-modules/zdf/component/rating-selector";
import { UtilityText } from "component/text";
import { CommentSliderFeatureConfig, CommentSliderVisualizationRedirect, CommentWithSlider, countRatings, Selector } from "feature/question/comment-slider";
import { historyGetState } from "platform-specific/url";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { MyAnswerPreview, QuestionConfig } from "structure/question";
// FIXME: Importing this isn't allowed in open-modules. Can we replace it with something else?
// eslint-disable-next-line open-modules-imports
import { useSessionStorage } from "usehooks-ts";
import { Datastore, useCollection, useDatastore, useGlobalProperty, useInstanceKey, useObject, usePersonaKey, useSessionData } from "util/datastore";
import { useConfig } from "util/features";
import { getScreenStackForUrl, pushSubscreen } from "util/navigate";
import { Feature, t } from "util/stdtypes";
import { logEventAsync } from "util/eventlog";
import { TextFieldButton } from "component/form";

export const CommentSliderComposerPreviewOnlyForCommentWithTextFeature: Feature<CommentConfig & CommentSliderFeatureConfig, {}> = {
    name: "Hide Composer for Slider-Only Votes",
    key: "commentslidernocomposerforslidervotes",
    providesConfig: {
        composerPreview: AnswerInputOnlyForCommentWithText,
    }
}

export const CommentSliderVoteFirstToShowResults: Feature<CommentSliderFeatureConfig & QuestionConfig, {}> = {
    name: "Slide first before submitting comment",
    key: "commentslider_vote_first_before_submitting_results",
    providesConfig: {
        commentSliderSelector: RatingSelectorWrapper,
        pageTopWidgets: [PreliminaryCommentSliderOrVisualization],
        commentPostCheckers: [commentPostChecker],
        commentDeleteTriggers: [cleanUpSessionStorage],
        commentPostTriggers: [cleanUpSessionStorage],
    }
}

export const CommentSliderVoteFirstToShowResultsAlternativeZDF: Feature<CommentConfig & CommentSliderFeatureConfig, {}> = {
    name: "Slide first before submitting comment (ZDF variant)",
    key: "commentslider_vote_first_before_submitting_results_variant_zdf",
    providesConfig: {
        commentSliderSelector: RatingSelectorWrapper,
        pageTopWidgets: [PreliminaryCommentSliderOrVisualizationAlternativeZDF],
        commentPostCheckers: [commentPostChecker],
        commentDeleteTriggers: [cleanUpSessionStorage],
        commentPostTriggers: [cleanUpSessionStorage],
    }
}

// This is used to set the vote, otherwise the default vote of three is set
async function commentPostChecker(datastore: Datastore, comment: Comment, questionText: string) {
    const userVote = getTemporaryCommentSliderVote({datastore});
    if (!(comment as CommentWithSlider).slider && userVote || (comment as CommentWithSlider).slider && userVote && (comment as CommentWithSlider).slider !== userVote) {
        return { allow: true, commentProps: { slider: userVote } };
    }
    return { allow: true }
}

async function cleanUpSessionStorage() {
    //TODO: Consider adding the keys and removing them one by one if more data is stored in the session storage
    sessionStorage.clear()
}

type SliderParams = {
    value: any, optionLabels: string[],
    optionColors: string[], defaultValue: number,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    placeholder: string, onChangeValue: Function,
    // This boolean should be removable, since login is always required currently
    shouldLoginOnSlide?: boolean
}

// Helper functions for retrieving the current unconfirmed vote
// TODO: SHould we use session Data instad (less persistent)
function getTemporaryCommentSliderVote({datastore}: {datastore: Datastore}) {
    const instanceKey = datastore.getInstanceKey();
    const vote  = sessionStorage.getItem("temp-vote-" + instanceKey)
    return vote as number | null
}

function useTemporaryCommentSliderVote() {
    const instanceKey = useInstanceKey()
    const [vote, setSessionVote] = useSessionStorage<number |undefined>("temp-vote-" + instanceKey, undefined);
    const setTemporaryCommentSliderVote = (value?: number) => {
        setSessionVote(value)
    }
    return [
        vote,
        setTemporaryCommentSliderVote
    ] as const
}

export function RatingSelectorWrapper({ value, optionLabels, optionColors, defaultValue, placeholder, onChangeValue }: SliderParams) {
    const [vote, setSessionVote] = useTemporaryCommentSliderVote();

    return (
        <RatingSelectorReworked
            value={vote ?? value ?? undefined}
            defaultValue={defaultValue}
            onChangeValue={async (v) => {
                await onChangeValue(v);
                setSessionVote(v);
            }}
            optionColors={optionColors}
            optionLabels={optionLabels}
            placeholder={placeholder}
        />
    );
}

function AnswerInputOnlyForCommentWithText() {
    const personaKey = usePersonaKey();
    const myAnswer = useCollection(commentTable, {filter: {from: personaKey, replyTo: null, deleted: null}})[0];

    if (myAnswer && myAnswer?.text !== "") {
        return <MyAnswerPreview comment={myAnswer} />
    }
    return null
}


function PreliminaryCommentSliderOrVisualization({comments}){
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    const myAnswer = useCollection(commentTable, {filter: {from: personaKey, replyTo: null, deleted: null}})[0];
    const [tempVote, _] = useTemporaryCommentSliderVote();

    const [editableComment, setEditableComment] = useState(null)
    const newComment = useMemo(() => ({text: '', key: datastore.getNewKey(), from: personaKey, time: datastore.getTime(), slider: tempVote}), [personaKey, tempVote]); 
    const finalComment = editableComment ?? myAnswer ?? newComment


    const [changeVote, setChangeVote] = useState(false)

    return <View>
        {((myAnswer as CommentWithSlider)?.slider && !changeVote) && <View style={{gap: 24,padding: 20, backgroundColor: colorSurfaceSecondary, marginTop: 32, borderRadius: 12}}>
            {<CommentSliderVisualizationRedirect comments={comments}></CommentSliderVisualizationRedirect>}
            {<Center>
                <PadBox vert={8}>
                   <CTAButton label={t("Change your vote")} onPress={() => setChangeVote(true)}/> 
                </PadBox>
            </Center>}
        </View>}
        {<View style={{gap: 24, paddingTop: 32, paddingHorizontal: 20, display: !((myAnswer as CommentWithSlider)?.slider && !changeVote) ? "flex" : "none"}}>  
            {<SelectorWithTemporaryVote comment={finalComment} setComment={setEditableComment} />}
            {(finalComment as any).slider && <SkipAndSubmitButton comment={finalComment} setComment={setEditableComment} onEditingDone={()=>{setChangeVote(false); setEditableComment(null)}}/>}
        </View>}
    </View>
}

function PreliminaryCommentSliderOrVisualizationAlternativeZDF({comments}){
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    const myComments = useCollection(commentTable, {filter: {from: personaKey, replyTo: null, deleted: null}});
    const myAnswer = myComments?.length > 0 ? myComments[0] : null

    const [tempVote, _] = useTemporaryCommentSliderVote();

    const [editableComment, setEditableComment] = useState(null)
    const newComment = useMemo(() => ({text: '', key: datastore.getNewKey(), from: personaKey, time: datastore.getTime(), slider: tempVote}), []); 
    const finalComment = editableComment ?? myAnswer ?? newComment

    const [changeVote, setChangeVote] = useState(false)
    const [openComposerAfterLogin, setAutoOpen] = useSessionStorage<boolean>('commentslider_auto_open_composer', false);

    const openComposer = (comment) => {
        setEditableComment(null);
        setChangeVote(false)
        datastore.pushSubscreen('composer', { commentKey: comment.key })
    }

    const onSlide = (comment)=>{
        if(personaKey) {
            openComposer(comment);
        } else {
            datastore.needsLogin(()=>{}, "")()
            setAutoOpen(true)
        }
    }

    // Execute the action the user has logged in for
    useEffect(()=>{
        if(personaKey && openComposerAfterLogin) {
            setAutoOpen(false)
            openComposer(finalComment);
        }
    }, [personaKey, openComposerAfterLogin])

    if((myAnswer as CommentWithSlider)?.slider && !changeVote) {
        return <View style={{gap: 24,padding: 20, backgroundColor: colorSurfaceSecondary, marginTop: 32, borderRadius: 12}}>
            {<CommentSliderVisualizationRedirect comments={comments}></CommentSliderVisualizationRedirect>}
            {<Center>
                <PadBox vert={8}>
                   <CTAButton label={t("Change your vote")} onPress={() => setChangeVote(true)}/> 
                </PadBox>
            </Center>}
        </View>
    }
    
    return <View style={{gap: 24, paddingTop: 32, paddingHorizontal: 20 }}>  
        {<SelectorWithTemporaryVote comment={finalComment} setComment={onSlide} />}
    </View>
}

export function SelectorWithTemporaryVote({ comment, setComment }) {
    const [myTemporaryVote, setMyTemporaryVote] = useTemporaryCommentSliderVote();
    const passthroughComment = (!comment.slider && myTemporaryVote) ? {...comment, slider: myTemporaryVote} : comment

    const setCommentPassthrough = (newComment) => {
        setComment?.(newComment);
        setMyTemporaryVote(newComment?.slider)
    }
    
    return <Selector comment={passthroughComment} setComment={setCommentPassthrough} showSubLabel={false}/>
}

export function SkipAndSubmitButton({comment, setComment, onEditingDone }) {
    const personaKey = usePersonaKey();
    const datastore = useDatastore();
    const [inProgress, setInProgress] = useState(false);
    const questionText = useGlobalProperty('name');
    const {commentPostBlockers,
        commentPostCheckers,
        commentPostTriggers,
        commentAllowEmpty,
    } = useConfig<CommentConfig>();

    const existingComment = useObject(commentTable, comment.key);

    const isBlocked = commentPostBlockers?.some(blocker => blocker(datastore, comment));
    const canPost = (comment.text || commentAllowEmpty) && !isBlocked;
    const isNewComment = !existingComment;
    
    // TODO: This a copy of the core functionality of posting.
    // This method should be refactored to a reuasable function
    async function storeCommentAndRunTriggersAsync(comment) {
        if (isNewComment) {
            const key = await datastore.addObjectWithKey(commentTable, comment.key, {...comment});
            logEventAsync(datastore, 'post-finish', {commentKey: comment.key, text: comment.text, comment});
        } else {
            logEventAsync(datastore, 'edit-finish', {commentKey: comment.key, text: comment.text, comment});
            await datastore.updateObject(commentTable, comment.key, {...comment, edited: Date.now()});
        }
        if (commentPostTriggers?.length) {
            // Don't await the promise, since some triggers may be slow
            Promise.all(commentPostTriggers.map(trigger => trigger({datastore, comment, commentKey: comment.key})));
        }
    }

    async function onPost() {
        logEventAsync(datastore, 'post-button-click', {commentKey: comment.key, text: comment.text});
        if (commentPostCheckers?.length) {
            setInProgress(true);
            const checkResults = await Promise.all(commentPostCheckers.map(checker =>
                checker(datastore, comment, questionText)
            ))
            let finalComment = {...comment};
            checkResults.forEach(judgement => {
                finalComment = {...finalComment, ...judgement.commentProps}
            })
            let modals : React.ComponentType<{onClose: () => void}>[] = [];
            checkResults.forEach(judgement => {
                if (judgement.modal) {
                    modals = [...modals, judgement.modal]
                }
            })
            if (checkResults.every(x => x.allow)) {
                await storeCommentAndRunTriggersAsync(finalComment);
                onEditingDone(finalComment);
            } else {
                setComment(finalComment);
            }
            setInProgress(false);
        } else {
            await storeCommentAndRunTriggersAsync(comment);
            onEditingDone(comment);
        }
    }

    const openComposer = () => {
        datastore.pushSubscreen('composer', { commentKey: comment.key })
        onEditingDone(comment)
    }

    // TODO: Please add a more elegant way to perform an action after a login
    const [autoOpenComposerAfterLogin, setAutoOpen] = useSessionStorage<boolean>('commentslider_auto_open_composer', false);
    const [autoSubmitCommentAfterLogin, setAutoSubmit] = useSessionStorage<boolean>('commentslider_auto_submit', false);

    useEffect(()=>{
        if(autoOpenComposerAfterLogin && personaKey) {
            setAutoOpen(false)
            openComposer()
        }
    }, [personaKey, autoOpenComposerAfterLogin])

    useEffect(()=>{
        if(autoSubmitCommentAfterLogin && personaKey) {
            setAutoSubmit(false)
            onPost()
        }
    }, [personaKey, autoSubmitCommentAfterLogin])


    return <>
        {<TextFieldButton placeholder={ existingComment ? t('Update your response') : t('Share your thoughts')} onPress={
            ()=>{
            datastore.needsLogin(() => {}, "")();
            if(personaKey) {
                openComposer()
            } else {
                setAutoOpen(true)
            }
        }
        }/>}
        <SubtleButton
            icon={Loop}
            label={t("Skip and submit vote")}
            color={colorButtonAccent}
            onPress={() => {
                datastore.needsLogin(() => {}, "")();
                if(!personaKey) {
                    setAutoSubmit(true)
                } else {
                    onPost()
                }
            }}
        />
    </>
}